// src/pages/LoginPage.js
import React, { useState } from 'react'; // Added useState
import { auth, signInWithMicrosoft } from '../firebase/config.js'; // Import auth and Microsoft auth
import { signInWithEmailAndPassword } from 'firebase/auth'; // Import for email/pass login
import reliefRosterLogo from '../assets/Relief_Roster_logo_med.png'; // Adjust the path to your image

// handleLogin prop is for Google Sign-In, authError prop is for general auth errors from App.js
const LoginPage = ({ handleLogin, authError }) => { 
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailPassError, setEmailPassError] = useState(''); // Specific error for email/password form
  const [isLoadingEmailPass, setIsLoadingEmailPass] = useState(false);
  const [isLoadingGoogle, setIsLoadingGoogle] = useState(false);
  const [isLoadingMicrosoft, setIsLoadingMicrosoft] = useState(false);

  // Styles for the main container
  const pageStyle = {
    backgroundImage: `url(${process.env.PUBLIC_URL}/blur.png)`,
    backgroundSize: 'cover',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    width: '100vw',
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    boxSizing: 'border-box',
    color: 'white', 
  };

  // Styles for the content box
  const contentBoxStyle = {
    backgroundColor: 'rgba(0, 0, 0, 0.6)', 
    padding: '30px 40px',
    borderRadius: '10px',
    textAlign: 'center',
    width: '100%', // Make it responsive
    maxWidth: '450px', 
    boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
  };

  const googleButtonStyle = {
    padding: '12px 25px',
    fontSize: '17px', // Slightly adjusted
    cursor: 'pointer',
    backgroundColor: '#4285F4', 
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    marginTop: '10px', // Adjusted margin
    width: '100%', // Make button full width of content box
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px' // For icon and text
  };

  const microsoftButtonStyle = {
    ...googleButtonStyle, // Inherit common styles
    backgroundColor: '#0078d4', // Microsoft blue
    marginTop: '10px', // Space between Google and Microsoft buttons
  };

  const emailPassButtonStyle = {
    ...googleButtonStyle, // Inherit common styles
    backgroundColor: '#5cb85c', // Green for email/pass sign in
    marginTop: '20px', // Margin after password field
  };

  const inputStyle = {
    width: '100%',
    padding: '12px', // Increased padding
    boxSizing: 'border-box',
    borderRadius: '4px',
    border: '1px solid #ccc',
    marginBottom: '15px', // Space between inputs
    fontSize: '16px'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '5px',
    fontWeight: 'bold',
    textAlign: 'left' // Align labels to the left
  };

  const errorStyle = {
    color: '#ffdddd', 
    border: '1px solid #ff7777',
    backgroundColor: 'rgba(255, 0, 0, 0.1)',
    padding: '10px',
    marginTop: '15px', // Changed from marginBottom to marginTop
    borderRadius: '5px',
    wordBreak: 'break-word' // Prevent long error messages from breaking layout
  };

  const orSeparatorStyle = {
    textAlign: 'center',
    margin: '25px 0',
    color: '#ccc', // Lighter color for the separator
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  };

  const lineStyle = {
    flexGrow: 1,
    height: '1px',
    backgroundColor: '#555' // Darker line for better visibility
  };

  const handleEmailPasswordSignIn = async (e) => {
    e.preventDefault();
    setEmailPassError(''); 
    setIsLoadingEmailPass(true);
    setIsLoadingGoogle(false); // Ensure other button loaders are off
    setIsLoadingMicrosoft(false);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // onAuthStateChanged in App.js will handle successful login.
    } catch (error) {
      console.error("LoginPage: Email/Password Sign-In Error:", error.code, error.message);
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        setEmailPassError('Invalid email or password. Please try again.');
      } else if (error.code === 'auth/invalid-email') {
        setEmailPassError('Please enter a valid email address.');
      } else if (error.code === 'auth/too-many-requests') {
        setEmailPassError('Access temporarily disabled due to too many failed login attempts. Please try again later or reset your password.');
      }
       else {
        setEmailPassError('Login failed. Please try again later.');
      }
    }
    setIsLoadingEmailPass(false);
  };

  const handleGoogleSignInInternal = async () => {
    setIsLoadingGoogle(true);
    setIsLoadingEmailPass(false); // Ensure other button loaders are off
    setIsLoadingMicrosoft(false);
    setEmailPassError(''); 
    await handleLogin(); // Calls the Google Sign-In handler passed from App.js
    setIsLoadingGoogle(false); 
  };

  const handleMicrosoftSignInInternal = async () => {
  setEmailPassError('');
  
  try {
    console.log("🔵 Starting Microsoft sign-in...");
    
    // Call signInWithPopup IMMEDIATELY - before any async state updates
    // This is critical for Safari to allow the popup
    const signInPromise = signInWithMicrosoft();
    
    // Now we can update loading state
    setIsLoadingMicrosoft(true);
    setIsLoadingGoogle(false);
    setIsLoadingEmailPass(false);
    
    // Wait for the sign-in to complete
    const result = await signInPromise;
    
    console.log("🔵 Microsoft sign-in result:", result);
    console.log("🔵 User email from Microsoft:", result.user.email);
    // onAuthStateChanged in App.js will handle successful login
  } catch (error) {
    console.error("🔵 Microsoft Sign-In Error:", error);
    
    if (error.message && error.message.includes("popup")) {
      setEmailPassError("Sign-in popup was blocked. Please allow popups for this site in your browser settings, then try again.");
    } else if (error.message && error.message.includes("already linked")) {
      setEmailPassError("This email is already linked to another sign-in method. Please use the same sign-in method you used previously.");
    } else if (error.message && error.message.includes("cancelled")) {
      // User closed the popup - don't show an error
      console.log("🔵 User cancelled sign-in");
    } else {
      setEmailPassError(error.message || 'Microsoft sign-in failed. Please try again.');
    }
  }
  setIsLoadingMicrosoft(false);
};

  const isAnyLoading = isLoadingEmailPass || isLoadingGoogle || isLoadingMicrosoft;

  return (
    <div style={pageStyle}>
      <div style={contentBoxStyle}>
        <img 
            src={`${process.env.PUBLIC_URL}/Relief_Roster_logo_med.png`} 
            alt="Relief Roster Logo" 
            style={{ width: '100px', height: 'auto', marginBottom: '20px' }} 
        />
        <h1 style={{ marginBottom: '10px', fontSize: '2em' }}>Relief Roster</h1>
        <p style={{ marginBottom: '30px', fontSize: '1.1em', color: '#f0f0f0' }}>
          Sign in to continue
        </p>

        {/* Display general authError from App.js (e.g., domain mismatch, claims issues) */}
        {authError && !emailPassError && ( // Only show if no specific email/pass error
          <p style={errorStyle}>
            {authError}
          </p>
        )}
        
        {/* Display specific email/password login error */}
        {emailPassError && (
          <p style={errorStyle}>
            {emailPassError}
          </p>
        )}

        <form onSubmit={handleEmailPasswordSignIn} style={{width: '100%'}}>
          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="email" style={labelStyle}>Email Address</label>
            <input 
              type="email" 
              id="email" 
              placeholder="Enter your email"
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              required 
              style={inputStyle}
              disabled={isAnyLoading}
            />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="password" style={labelStyle}>Password</label>
            <input 
              type="password" 
              id="password" 
              placeholder="Enter your password"
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
              required 
              style={inputStyle}
              disabled={isAnyLoading}
            />
          </div>
          <button 
            type="submit" 
            style={emailPassButtonStyle}
            disabled={isAnyLoading}
          >
            {isLoadingEmailPass ? 'Signing In...' : 'Sign In with Email'}
          </button>
        </form>
        
        <div style={orSeparatorStyle}>
            <div style={lineStyle}></div>
            <span style={{ padding: '0 10px' }}>OR</span>
            <div style={lineStyle}></div>
        </div>

        <button
          onClick={handleGoogleSignInInternal}
          style={googleButtonStyle}
          disabled={isAnyLoading}
        >
          {/* Google Icon SVG */}
          <svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="18px" height="18px" viewBox="0 0 48 48"><g><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24s.92 7.54 2.56 10.78l7.97-6.19z"></path><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path><path fill="none" d="M0 0h48v48H0z"></path></g></svg>
          {isLoadingGoogle ? 'Signing In...' : 'Sign in with Google'}
        </button>

        <button
          onClick={handleMicrosoftSignInInternal}
          style={microsoftButtonStyle}
          disabled={isAnyLoading}
        >
          {/* Microsoft Icon SVG */}
          <svg width="18px" height="18px" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1h9.5v9.5H1V1z" fill="#f25022"/>
            <path d="M12.5 1H22v9.5h-9.5V1z" fill="#00a4ef"/>
            <path d="M1 12.5h9.5V22H1v-9.5z" fill="#ffb900"/>
            <path d="M12.5 12.5H22V22h-9.5v-9.5z" fill="#7fba00"/>
          </svg>
          {isLoadingMicrosoft ? 'Signing In...' : 'Sign in with Microsoft'}
        </button>
      </div>
    </div>
  );
};

export default LoginPage;